/**
 * Admin section pages — thin wrappers that mount the existing section
 * components at their new dedicated routes under /admin/*.
 *
 * Phase 1 of the App Shell Redesign retired the legacy two-axis
 * tab/sidebar layout in `AdminPortal.jsx`. Each section now lives at
 * its own URL so admins can deep-link, bookmark, and navigate via
 * browser back/forward without the localStorage `activeSection` dance.
 *
 * The section components themselves were NOT modified — they keep
 * rendering exactly the same UI they always did. The work here is
 * purely routing + chrome.
 */
import React from "react";

import AccountsManagementSection from "@/components/admin/AccountsManagementSection";
import CreditsAdminSection from "@/components/admin/CreditsAdminSection";
import PlatformFeesSection from "@/components/admin/PlatformFeesSection";
import {
  PartnerTiersSection,
  CustomerPlansSection,
} from "@/components/admin/AdminTaxonomySections";
import TierInterestSection from "@/components/admin/TierInterestSection";
import SecuritySettingsSection from "@/components/admin/SecuritySettingsSection";
import PlatformSettingsForm from "@/components/admin/PlatformSettingsForm";
import AdminComingSoon from "@/components/admin/AdminComingSoon";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";

// ---- Real (relocated) section pages ---------------------------------------

export function AccountsAdminPage() {
  return <AccountsManagementSection />;
}

export function PooBucksAdminPage() {
  return <CreditsAdminSection />;
}

export function FeesAdminPage() {
  return <PlatformFeesSection />;
}

export function TiersAndPlansAdminPage() {
  // Combined screen: PooPartner Tiers stacked above Customer Plans
  // stacked above Tier interest (the "Save my spot" captures — read-
  // only v1 admin surface for the Pluckonomics tier reservation
  // flow). Each subsection owns its own loading + edit state — no
  // shared wrapper card so each renders with its full existing
  // layout. Vertical spacing is the only added affordance.
  return (
    <div className="space-y-12" data-testid="tiers-and-plans-admin-page">
      <PartnerTiersSection />
      <CustomerPlansSection />
      <TierInterestSection />
    </div>
  );
}

export function SettingsAdminPage() {
  // Combined screen: Platform Settings form + Security settings.
  // Security folds in here per the discovery decision (D1 section
  // recommendation accepted). The two sections render stacked with a
  // generous gap so each reads as its own card group.
  return (
    <div className="space-y-12" data-testid="settings-admin-page">
      <PlatformSettingsForm />
      <SecuritySettingsSection />
    </div>
  );
}

// ---- Stub pages (Phase 1 placeholders) -------------------------------------

import AdminDashboardPage from "./AdminDashboardPage";
import AdminBookingsPage from "./AdminBookingsPage";

export function DashboardAdminPage() {
  return <AdminDashboardPage />;
}

export function BookingsAdminPage() {
  return <AdminBookingsPage />;
}

export function AnalyticsAdminPage() {
  return <AdminAnalyticsPage />;
}

export function PooPartnersAdminPage() {
  return (
    <AdminComingSoon
      title="PooPartners"
      eta="Partner roster + onboarding pipeline — coming soon"
    />
  );
}
